import React from 'react';
import clsx from 'clsx';
import { Loader2 } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { CodeText } from './CodeText';

/**
 * Something the screen shows — a slot, a product, a box — as a button that
 * reads it: the tap hands `code` to `onTap`, which reads it as scanning it
 * would. Its name is the text it shows. While what the tap sent is running it
 * says so, and while the screen takes no read it is disabled: a tap never
 * does nothing in silence.
 *
 * @param {object} props
 * @param {string} props.code - What a scan of it would read.
 * @param {string} props.label - What it shows, e.g. the slot's name.
 * @param {React.ReactNode} [props.detail] - A second line.
 * @param {React.ReactNode} [props.aside] - At the right, e.g. «hay 179 un».
 * @param {React.ComponentType} [props.icon]
 * @param {'md'|'lg'} [props.size='md']
 * @param {boolean} [props.pill=false] - A chip, for the second choices of a list.
 * @param {string} [props.labelClassName]
 * @param {boolean} [props.disabled=false]
 * @param {boolean} [props.loading=false] - What the tap sent is running.
 * @param {(code: string) => void} props.onTap
 * @param {string} props.testId
 */
export const CodeChoice = ({
  code,
  label,
  detail,
  aside,
  icon: Icon,
  size = 'md',
  pill = false,
  labelClassName,
  disabled = false,
  loading = false,
  onTap,
  testId,
  ...rest
}) => {
  const LeadIcon = loading ? Loader2 : Icon;
  return (
    <Button
      variant="choice"
      pill={pill}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      onClick={() => onTap(code)}
      data-testid={testId}
      data-code={code}
      {...rest}
    >
      <span className="flex items-center gap-2">
        {LeadIcon && (
          <LeadIcon
            size={size === 'lg' ? 22 : 18}
            aria-hidden="true"
            className={clsx('shrink-0 text-ninja-green', loading && 'animate-spin')}
          />
        )}
        <span className="min-w-0 flex-1">
          <span
            className={clsx(
              'block font-bold',
              size === 'lg' ? 'text-2xl' : pill ? 'text-sm' : 'text-base',
              labelClassName
            )}
          >
            <CodeText text={label} />
          </span>
          {detail && <span className="block text-sm font-normal text-text-secondary">{detail}</span>}
        </span>
        {aside && <span className="shrink-0 text-sm font-semibold text-text-secondary">{aside}</span>}
      </span>
    </Button>
  );
};
